import {useRouter} from 'next/router';
import {useEffect, useMemo, useState} from 'react';
import {HOME} from '../../../config/constants/routes';
import type {GetUnfinishedEntryPromptResponse} from '../../../core/entities/recruitment/schema';
import {useGetUnfinishedEntryPrompt} from '../../../core/repositories/recruitmentRepository';
import {useAuth} from '../../../core/usecases/auth/AuthContext';
import {useIsAppWebView} from '../../../shared/hooks/useIsAppWebView';
import {
  UNFINISHED_ENTRY_PROMPT_ITEM_LIMIT,
  canRequestUnfinishedEntryPrompt,
} from './canRequestUnfinishedEntryPrompt';
import {
  CHECKED_SUPPRESSION_DAYS,
  SHOWN_SUPPRESSION_DAYS,
  canShowUnfinishedEntryPrompt,
  suppressUnfinishedEntryPrompt,
} from './unfinishedEntryPromptSuppression';

type Recruitments = GetUnfinishedEntryPromptResponse['recruitments'];

type Args = {
  // 優先度が上のポップアップと同時に開かないよう、呼び出し側から表示状態を受け取る
  isHigherPriorityPromptOpened: boolean;
  // 優先度が上のポップアップの表示判定がAPI解決待ちの間に先行して開かないよう受け取る
  isHigherPriorityPromptDeciding: boolean;
};

export const useUnfinishedEntryPrompt = ({
  isHigherPriorityPromptOpened,
  isHigherPriorityPromptDeciding,
}: Args) => {
  const {status, user} = useAuth();
  const isAppWebView = useIsAppWebView();
  const router = useRouter();
  // ストレージに保存できない環境でも閉じたあと再表示しないよう、抑止期限はメモリにも持つ
  const [suppressedUntil, setSuppressedUntil] = useState<Date | null>(null);
  // 表示した時点で抑止期限を進めるため、開いている間は抑止状態と別にこの状態で表示を保つ
  const [isOpen, setIsOpen] = useState(false);
  // 閉じるアニメーションの間も中身を出し続けるため、閉じても最後に表示した募集を残す
  const [shownRecruitments, setShownRecruitments] = useState<Recruitments>([]);
  // 表示のたびにモーダルを作り直し、表示計測を毎回送るために使う
  const [showCount, setShowCount] = useState(0);

  const isHome = router.pathname === HOME;
  const now = new Date();
  const isSuppressed =
    (suppressedUntil !== null && now < suppressedUntil) ||
    !canShowUnfinishedEntryPrompt(now);
  const canRequest =
    !isOpen &&
    canRequestUnfinishedEntryPrompt({
      isAuthenticated: status === 'authenticated',
      isAppWebView,
      pathname: router.pathname,
      isSuppressed,
    });

  const {data, isFetching} = useGetUnfinishedEntryPrompt({
    userId: user?.id,
    config: {enabled: canRequest},
  });
  // キャッシュが古いと既にエントリーした募集を出しうるため、再取得が終わるまで判定しない
  const recruitments = data?.ok ? data.value.recruitments : undefined;
  const candidates = useMemo(
    () =>
      canRequest && !isFetching && recruitments
        ? recruitments.slice(0, UNFINISHED_ENTRY_PROMPT_ITEM_LIMIT)
        : [],
    [canRequest, isFetching, recruitments],
  );
  const isBlocked =
    isHigherPriorityPromptOpened || isHigherPriorityPromptDeciding;

  useEffect(() => {
    if (isBlocked || candidates.length === 0) {
      return;
    }
    setIsOpen(true);
    setShownRecruitments(candidates);
    setShowCount((count) => count + 1);
    setSuppressedUntil(suppressUnfinishedEntryPrompt(SHOWN_SUPPRESSION_DAYS));
  }, [isBlocked, candidates]);

  // ホームを離れたら閉じる。戻ってきたときは抑止期限で判定し直す
  useEffect(() => {
    if (!isHome) {
      setIsOpen(false);
    }
  }, [isHome]);

  return {
    opened: isOpen && isHome,
    // 表示が確定する前に下位のポップアップが先に開いてしまうのを防ぐ
    isDeciding: canRequest && (isFetching || candidates.length > 0),
    recruitments: shownRecruitments,
    showCount,
    close(withSuppression: boolean) {
      setIsOpen(false);
      if (withSuppression) {
        setSuppressedUntil(
          suppressUnfinishedEntryPrompt(CHECKED_SUPPRESSION_DAYS),
        );
      }
    },
  };
};
